const PGAdapter = require('./../../common/pg-adapter')
const omitBy = require('lodash/omitBy')
const isNil = require('lodash/isNil')
const { SHIPMENT_STATUS } = require('../constants')
const { buildOtp } = require('../utils/utils')

const DATA_SHIPMENTSNAPSHOT_TABLE_NAME = 'avocado.data_shipmentsnapshot'
const DATA_SHIPMENTSNAPSHOT_COLUMNS = [
  'id',
  'shipment_id',
  'shipment_number',
  'status',
  'route_id',
  'funder_id',
  'program_id',
  'vendor_id',
  'origin_id',
  'destination_id',
  'notes',
  'is_automatic_return',
  'parent_doc_id',
  'order_id',
  'created_at',
  'updated_at',
  'created_by',
  'couchdb_id',
  'suborder_id',
  'is_gps_authenticated',
  'gps_validation_fail_comment',
  'gps_validation',
  'raw_order_id'
]

const DATA_SHIPMENTSNAPSHOT_CHANGE_TABLE_NAME = 'avocado.data_shipmentsnapshot_change'
const DATA_SHIPMENT_COMMENT_TABLE_NAME = 'avocado.data_shipment_comment'
const DATA_SHIPMENT_COLLECTOR_TABLE_NAME = 'avocado.data_shipment_collector'

// `getShipmentDocRows` returns its rows inside jsonb, which renders a
// `timestamptz` with a `+00:00` offset rather than the `Z` that pg's own Date
// parsing plus `toISOString()` produced. Rendering in SQL instead keeps the
// rebuilt documents carrying byte-identical strings: `to_char` in UTC with
// millisecond precision is exactly `Date.prototype.toISOString()`'s format.
// Fixed width also means ordering by the text is still chronological.
const isoUtc = column => `to_char(${column} AT TIME ZONE 'UTC', 'YYYY-MM-DD"T"HH24:MI:SS.MS"Z"')`

class ShipmentSnapshotAdapter extends PGAdapter {
  constructor (pgConnection, username, logger, options = {}) {
    super(
      pgConnection,
      DATA_SHIPMENTSNAPSHOT_TABLE_NAME,
      username,
      DATA_SHIPMENTSNAPSHOT_COLUMNS,
      'id',
      null,
      logger,
      { strictColumns: false, withGlobalAccessUser: options.withGlobalAccessUser }
    )
  }

  /**
   * Get CouchDB IDs and location IDs for shipment snapshots by route IDs
   * @param {string[]} routeIds - Array of route IDs to filter by
   * @param {object} options - Query options
   * @param {string[]} [options.programIds] - Filter by program IDs
   * @param {string} [options.cutoffDate] - ISO date string (e.g., '2024-01-01T00:00:00.000Z')
   * @returns {Promise<{couchdbIds: string[], locationIds: string[]}>} Object with couchdbIds and unique locationIds (origins + destinations)
   */
  async getCouchdbIdsByRoutes (routeIds, options = {}) {
    if (!Array.isArray(routeIds)) {
      throw new Error('routeIds must be an array')
    }
    if (routeIds.length === 0) {
      return { couchdbIds: [], locationIds: [], funderIds: [], serviceIds: [], productIds: [] }
    }

    const { programIds = [], cutoffDate = null } = options

    const params = []
    let paramIndex = 1

    let query = `
      SELECT ss.couchdb_id,
             ss.origin_id,
             ss.destination_id,
             ss.funder_id,
             s.fsid as service_id,
             ARRAY_AGG('product:' || ssl.sku) AS skus
      FROM ${DATA_SHIPMENTSNAPSHOT_TABLE_NAME} ss
      LEFT JOIN avocado.data_shipmentsnapshotline ssl ON ss.id = ssl.shipment_snapshot_id
      LEFT JOIN avocado.data_program p ON ss.program_id = p.fsid
      LEFT JOIN avocado.data_service s ON p.id = s.program_id
      WHERE ss.couchdb_id IS NOT NULL
        AND ss.route_id = ANY($${paramIndex++}::text[])
    `

    params.push(routeIds)

    if (programIds.length > 0) {
      query += ` AND ss.program_id = ANY($${paramIndex++}::text[])`
      params.push(programIds)
    }

    if (cutoffDate) {
      query += ` AND ss.updated_at >= $${paramIndex++}`
      params.push(cutoffDate)
    }

    // We add the Group by clause after adding potential filters
    query += ` GROUP BY ss.couchdb_id, ss.origin_id, ss.destination_id, ss.funder_id, s.fsid`

    try {
      const result = await this.pgConnection.query(query, params)

      const couchdbIds = result.rows.map(row => row.couchdb_id)
      // Dedupe location IDs because multiple shipments may share the same origin/destination.
      // These IDs are used for offline locationMatch filtering in the van-shipments dispenser.
      const locationSet = new Set()
      const skuSet = []
      const funderSet = new Set()
      const serviceSet = new Set()

      result.rows.forEach(row => {
        if (row.origin_id) locationSet.add(row.origin_id)
        if (row.destination_id) locationSet.add(row.destination_id)
        if (row.skus && Array.isArray(row.skus)) {
          skuSet.push(...row.skus)
        }
        if (row.funder_id) funderSet.add(row.funder_id)
        if (row.service_id) serviceSet.add(row.service_id)
      })

      return {
        couchdbIds,
        locationIds: Array.from(locationSet),
        funderIds: Array.from(funderSet),
        serviceIds: Array.from(serviceSet),
        productIds: [...new Set(skuSet)]
      }
    } catch (error) {
      if (this.logger) {
        this.logger.error({
          code: 'shipment-snapshot-query-failed',
          routeIds,
          programIds,
          cutoffDate,
          error: error.message
        })
      }
      throw error
    }
  }

  /**
   * Get LMD snapshot enrichment data from PostgreSQL.
   *
   * @param {string[]} shipmentIds - Array of shipment_id values
   * @returns {Promise<Object[]>} Array of enrichment objects keyed by shipment_id
   */
  async getLmdSnapshotData (shipmentIds) {
    if (!shipmentIds.length) return []

    const snapshotQuery = `
      SELECT
        ss.shipment_id,
        ss.couchdb_id,
        ss.status,
        ss.created_at,
        ss.created_by,
        ss.parent_doc_id,
        ss.raw_order_id,
        ss.is_gps_authenticated,
        ss.gps_validation_fail_comment,
        ss.gps_validation,
        (
          SELECT jsonb_object_agg(
            ssld.batch,
            jsonb_build_object('quantity', ssld.amount)
          )
          FROM avocado.data_shipmentsnapshotline ssl
          JOIN avocado.data_shipmentsnapshotline_details ssld ON ssld.shipmentsnapshot_line = ssl.id
          WHERE ssl.shipment_snapshot_id = ss.id
        ) AS counts
      FROM ${DATA_SHIPMENTSNAPSHOT_TABLE_NAME} ss
      WHERE ss.shipment_id = ANY($1)
      ORDER BY ss.shipment_id, ss.created_at ASC
    `

    const otpQuery = `
      SELECT
        so.shipment_id,
        so.password,
        so.otp_key,
        so.user_fail_reason,
        so.verified
      FROM avocado.shipment_otp so
      WHERE so.shipment_id = ANY($1)
    `

    // One row per shipment: the latest collector, so a shipment with collector
    // docs on several hand-off snapshots reads the most recent identity.
    const collectorQuery = `
      SELECT DISTINCT ON (shipment_id)
        shipment_id,
        collector_type,
        name,
        phone_number
      FROM ${DATA_SHIPMENT_COLLECTOR_TABLE_NAME}
      WHERE shipment_id = ANY($1)
      ORDER BY shipment_id, created_at DESC, couchdb_id DESC
    `

    const [snapshotResult, otpResult, collectorResult] = await Promise.all([
      this.pgConnection.query(snapshotQuery, [shipmentIds]),
      this.pgConnection.query(otpQuery, [shipmentIds]),
      this.pgConnection.query(collectorQuery, [shipmentIds])
    ])

    const otpByShipment = {}
    for (const row of otpResult.rows) {
      otpByShipment[row.shipment_id] = row
    }

    const collectorByShipment = {}
    for (const row of collectorResult.rows) {
      collectorByShipment[row.shipment_id] = {
        name: row.name,
        phoneNumber: row.phone_number,
        type: row.collector_type
      }
    }

    const shipmentMap = {}
    for (const row of snapshotResult.rows) {
      const entry = shipmentMap[row.shipment_id] || { snapshotDates: {}, history: {} }
      shipmentMap[row.shipment_id] = entry

      const createdAtISO = row.created_at.toISOString()

      // snapshotDates: earliest date wins per status
      if (!entry.snapshotDates[row.status] || createdAtISO < entry.snapshotDates[row.status]) {
        entry.snapshotDates[row.status] = createdAtISO
      }

      // history: keyed by couchdb_id
      if (row.couchdb_id) {
        entry.history[row.couchdb_id] = omitBy({
          createdAt: createdAtISO,
          createdBy: { user: row.created_by, agent: '' },
          counts: row.counts || {},
          parentDocId: row.parent_doc_id
        }, isNil)
      }

      entry.orderId = row.raw_order_id
      entry.isGpsAuthenticated = row.is_gps_authenticated
      entry.gpsValidationFailComment = row.gps_validation_fail_comment
      entry.gpsValidation = row.gps_validation

      if (row.status === SHIPMENT_STATUS.RECEIVED) {
        entry.driverName = row.created_by
      }
    }

    return shipmentIds.map(shipmentId => {
      const entry = shipmentMap[shipmentId]
      if (!entry) return null

      const otpRow = otpByShipment[shipmentId]
      if (otpRow) {
        entry.otp = buildOtp(otpRow)
      }

      const collector = collectorByShipment[shipmentId]
      if (collector) {
        entry.collector = collector
      }

      return { id: shipmentId, ...entry }
    }).filter(Boolean)
  }

  /**
   * Get CouchDB IDs for change documents linked to the specified snapshots
   * @param {string[]} snapshotCouchdbIds - Array of snapshot CouchDB IDs
   * @returns {Promise<string[]>} Array of change document CouchDB IDs
   */
  async getChangeDocIds (snapshotCouchdbIds) {
    if (!Array.isArray(snapshotCouchdbIds) || snapshotCouchdbIds.length === 0) {
      return []
    }

    const query = `
      SELECT sc.couchdb_id
      FROM ${DATA_SHIPMENTSNAPSHOT_CHANGE_TABLE_NAME} sc
      WHERE sc.snapshot_couchdb_id = ANY($1::text[])
    `

    try {
      const result = await this.pgConnection.query(query, [snapshotCouchdbIds])
      return result.rows.map(row => row.couchdb_id)
    } catch (error) {
      if (this.logger) {
        this.logger.error({
          code: 'shipment-change-query-failed',
          snapshotCouchdbIds,
          error: error.message
        })
      }
      throw error
    }
  }

  /**
   * Every Postgres row that makes up one shipment, in the shape the couch
   * document set has: snapshots (with batch-level counts), change documents
   * (with their lines and vector clocks), comments and the OTP row.
   *
   * Deliberately raw — `pgRowsToShipmentDocs` turns these into couch-shaped
   * documents and `docsToShipmentRecords` does the merging, so winner
   * selection and the vector-clock ordering stay in one implementation
   * rather than being reimplemented in SQL.
   *
   * One statement, one pool connection. The four sets are tagged with a `kind`
   * and stacked with UNION ALL rather than joined together: only `changes`
   * hangs off `snapshots`, while `comments` and `otp` are keyed on
   * `shipment_id` independently. Joining siblings is a cross product, so a
   * single joined query would return (changes per snapshot x comments) rows and
   * repeat each snapshot's `counts` map and each comment's inline base64 ePOD
   * signature in every duplicated row. Those joins would also all have to be
   * LEFT: a shipment with no comments is normal, and an inner join would return
   * no rows for it, which `findFromPg` reports as empty and the controller turns
   * into a 404 on a shipment that exists. Stacking keeps the row count at
   * snapshots + changes + comments + 1 with nothing duplicated.
   *
   * @param {string} shipmentId - the couch shipment id (prefix before `:status:`)
   * @returns {Promise<{snapshots: object[], changes: object[], comments: object[], otp: object|null}>}
   */
  async getShipmentDocRows (shipmentId) {
    // Change lines are aggregated into the `changes` map shape the couch change
    // document uses, so the mapper stays a straight field rename.
    //
    // Changes are scoped by joining the snapshot row's `shipment_id` rather than
    // by prefix matching `snapshot_couchdb_id`: shipment ids are not prefix-free
    // (a `...:shipmentNo:806` id is a prefix of `...:shipmentNo:8061`), so a
    // LIKE would pull another shipment's change docs into this merge.
    //
    // Both the `shipmentsnapshot_id` join and the `snapshot_change_id` lines
    // lookup are foreign keys that 0382 left unindexed; migration 0484 indexes
    // them, without which this read scans both change tables in full.
    //
    // The otp row carries no `password`: it is the plaintext secret handed to
    // the recipient out of band, and these rows are rebuilt into an entity that
    // can go to the driver's browser. `verified` carries what the read path
    // actually needs — whether the code was entered and matched. See
    // pg-rows-to-shipment-docs.
    const query = `
      WITH snapshot_rows AS (
        SELECT
          ss.couchdb_id,
          ss.status,
          ${isoUtc('ss.created_at')} AS created_at,
          ${isoUtc('ss.updated_at')} AS updated_at,
          ss.created_by,
          ss.parent_doc_id,
          ss.order_id,
          ss.raw_order_id,
          ss.program_id,
          ss.funder_id,
          ss.route_id,
          ss.vendor_id,
          ss.is_automatic_return,
          ss.is_gps_authenticated,
          ss.gps_validation_fail_comment,
          ss.gps_validation,
          (
            SELECT jsonb_object_agg(
              ssld.batch,
              jsonb_build_object('quantity', ssld.amount)
            )
            FROM avocado.data_shipmentsnapshotline ssl
            JOIN avocado.data_shipmentsnapshotline_details ssld ON ssld.shipmentsnapshot_line = ssl.id
            WHERE ssl.shipment_snapshot_id = ss.id
          ) AS counts
        FROM ${DATA_SHIPMENTSNAPSHOT_TABLE_NAME} ss
        WHERE ss.shipment_id = $1
      ),
      change_rows AS (
        SELECT
          sc.couchdb_id,
          sc.snapshot_couchdb_id,
          sc.device_id,
          sc.version,
          sc.clock,
          ${isoUtc('sc.created_at')} AS created_at,
          ${isoUtc('sc.updated_at')} AS updated_at,
          sc.created_by,
          (
            SELECT jsonb_object_agg(
              scl.batch_id,
              jsonb_strip_nulls(jsonb_build_object(
                'quantity', scl.quantity,
                'checked', scl.checked,
                'timestamp', scl.timestamp,
                'paymentType', scl.payment_type,
                'removed', scl.removed
              ))
            )
            FROM avocado.data_shipmentsnapshot_change_lines scl
            WHERE scl.snapshot_change_id = sc.id
          ) AS changes
        FROM ${DATA_SHIPMENTSNAPSHOT_CHANGE_TABLE_NAME} sc
        JOIN ${DATA_SHIPMENTSNAPSHOT_TABLE_NAME} ss ON ss.id = sc.shipmentsnapshot_id
        WHERE ss.shipment_id = $1
      ),
      comment_rows AS (
        SELECT
          c.couchdb_id,
          ${isoUtc('c.created_at')} AS created_at,
          c.created_by,
          c.comment,
          c.signature,
          c.name,
          c.adjustment_reasons
        FROM ${DATA_SHIPMENT_COMMENT_TABLE_NAME} c
        WHERE c.shipment_id = $1
      ),
      collector_rows AS (
        SELECT
          col.couchdb_id,
          ${isoUtc('col.created_at')} AS created_at,
          col.created_by,
          col.collector_type,
          col.name,
          col.phone_number,
          col.signature,
          col.comment
        FROM ${DATA_SHIPMENT_COLLECTOR_TABLE_NAME} col
        WHERE col.shipment_id = $1
      ),
      otp_rows AS (
        SELECT so.shipment_id, so.otp_key, so.user_fail_reason, so.verified
        FROM avocado.shipment_otp so
        WHERE so.shipment_id = $1
      )
      SELECT 'snapshot' AS kind, to_jsonb(s) AS doc, s.created_at AS sort_key
      FROM snapshot_rows s
      UNION ALL
      SELECT 'change' AS kind, to_jsonb(c) AS doc, c.created_at AS sort_key
      FROM change_rows c
      UNION ALL
      SELECT 'comment' AS kind, to_jsonb(m) AS doc, m.created_at AS sort_key
      FROM comment_rows m
      UNION ALL
      SELECT 'collector' AS kind, to_jsonb(col) AS doc, col.created_at AS sort_key
      FROM collector_rows col
      UNION ALL
      SELECT 'otp' AS kind, to_jsonb(o) AS doc, NULL::text AS sort_key
      FROM otp_rows o
      ORDER BY kind, sort_key ASC
    `

    const {rows} = await this.pgConnection.query(query, [shipmentId])

    // Ordering by `kind` groups each set's rows contiguously and `sort_key`
    // (the fixed-width UTC ISO string) orders within it, so collecting in row
    // order reproduces the `created_at ASC` each separate query returned.
    const docsByKind = rows.reduce((acc, {kind, doc}) => {
      acc[kind].push(doc)
      return acc
    }, {snapshot: [], change: [], comment: [], collector: [], otp: []})

    return {
      snapshots: docsByKind.snapshot,
      changes: docsByKind.change,
      comments: docsByKind.comment,
      collectors: docsByKind.collector,
      otp: docsByKind.otp[0] || null
    }
  }
}

module.exports = ShipmentSnapshotAdapter
